import { Component, OnDestroy, OnInit } from '@angular/core';
import {
   AbstractControl,
   FormControl,
   FormGroup,
   ValidationErrors,
   Validators
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AppComponent } from '../app.component';
import { checkStringIsDate } from '../shared/date-helper';
import { PersonRedirect } from '../shared/enums/redirectDecision';
import { supportedLangToDepoLang, tranlateToLanguage } from '../shared/helper-functions';
import { MediaService } from '../shared/services/media.service';
import { SupportedLanguages } from '../shared/types/language.type';
import { HomeService } from './home.service';

@Component({
   selector: 'app-home',
   templateUrl: './home.component.html',
   styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
   destroyed$ = new Subject<void>();

   currentLang = 'hu';

   startDate = new Date('2000.01.01');

   touchUi$ = new BehaviorSubject(false);

   form = new FormGroup({
      taxNumber: new FormControl('', [Validators.required, this.taxnumberValidator.bind(this)]),
      dateOfBirth: new FormControl('', Validators.required)
   });

   constructor(
      private router: Router,
      public translate: TranslateService,
      private mediaService: MediaService,
      private homeService: HomeService,
      private snackBar: MatSnackBar
   ) {
      this.mediaService.screen$
         .pipe(
            takeUntil(this.destroyed$),
            map(screen => {
               return (
                  screen === 'mobileM' ||
                  screen === 'phoneLandscape' ||
                  screen === 'phonePortrait' ||
                  screen === 'tabletPortrait'
               );
            })
         )
         .subscribe(this.touchUi$);
   }

   ngOnInit() {
      AppComponent.homeFormData = undefined;
      this.currentLang = this.translate.currentLang;
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   onNext(language: SupportedLanguages) {
      // the communication language also becomes the UI language
      this.translate.use(language);
      AppComponent.currentLang = language;
      this.currentLang = language;

      AppComponent.homeFormData = { ...this.form.value, language };
      this.homeService
         .getStudentStatus(
            this.form.value.taxNumber,
            AppComponent.uuid,
            AppComponent.homeFormData?.dateOfBirth
               ? format(AppComponent.homeFormData?.dateOfBirth, 'yyyy-MM-dd')
               : undefined,
            supportedLangToDepoLang(language)
         )
         .subscribe({
            next: response => {
               AppComponent.uuid = response.uuid;
               switch (response.status) {
                  case PersonRedirect.VALID:
                     this.router.navigate(['valid']);
                     break;
                  case PersonRedirect.REGISTER:
                     this.router.navigate(['info']);
                     break;
                  case PersonRedirect.MEMBERSHIP_VALIDATION:
                  case PersonRedirect.DATA_SUPPLEMENTATION:
                     this.router.navigate(['membership-validation-info']);
                     AppComponent.homeFormData!.useCase = response.status;
                     break;
                  case PersonRedirect.NO_ACTION:
                     this.router.navigate(['countryside']);
                     break;
                  default:
                     this.router.navigate(['countryside']);
                     break;
               }
            },
            error: () => {
               this.snackBar.open(this.bilingual('generalError'), 'OK', {
                  duration: 5000
               });
            }
         });
   }

   // the home page is shown in both languages, independently of the UI language
   inLang(key: string, lang: SupportedLanguages): string {
      return this.translate.translations[lang] ? tranlateToLanguage(this.translate, key, lang) : '';
   }

   bilingual(key: string): string {
      return `${this.inLang(key, 'hu')} / ${this.inLang(key, 'en')}`;
   }

   setLang() {
      if (this.currentLang === 'en') {
         this.translate.use('hu');
         this.currentLang = 'hu';
      } else {
         this.translate.use('en');
         this.currentLang = 'en';
      }
   }

   dateOfBirtOnChange($event: any) {
      const result = checkStringIsDate($event);
      if (result) {
         this.form.controls.dateOfBirth.setValue(result);
      }
   }

   taxnumberValidator(control: AbstractControl): ValidationErrors | null {
      let errors: any = null;

      const value: string | null = control.value;
      if (!value) {
         return errors;
      }

      const length = value.length;

      if (length > 0 && value.charAt(0) !== '8') {
         errors = { ...errors, leadingEight: true };
      }

      if (length > 6) {
         const days = +value.slice(1, 6);
         const bod: Date = new Date(this.form.controls.dateOfBirth.value);
         bod.setHours(0);
         bod.setUTCMinutes(-bod.getTimezoneOffset(), 0, 0);

         const referenceDate = new Date('1867-01-01');
         const diffInDays = (bod.getTime() - referenceDate.getTime()) / 86400000;

         if (diffInDays !== days) {
            errors = { ...errors, badDays: true };
         }
      }

      if (length === 10) {
         const checkSum = [...value.slice(0, -1)].reduce((acc, val, i) => {
            return acc + (i + 1) * +val;
         }, 0);

         if (checkSum % 11 !== +value.charAt(9)) {
            errors = { ...errors, badCheckSum: true };
         }
      } else {
         errors = { ...errors, length: true };
      }

      return errors;
   }
}
